import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3105";

export const U3105_MIN_PAYROLL_PERIODS = 1;
export const U3105_MAX_PAYROLL_PERIODS = 27;

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3105InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
}

export interface U3105PayrollYearSearchRow {
  id: string;
  yearCode?: string;
  version: number;
  deletedAt?: string | null;
  createdAt?: string;
}

export interface U3105PayrollPeriodApi {
  id?: string;
  periodNumber: number;
  startAt: string;
  endAt: string;
  version?: number;
}

/** GET /U3105/:id — sans organization */
export interface U3105PayrollYearGet {
  id: string;
  yearCode: string;
  version: number;
  payrollPeriods: U3105PayrollPeriodApi[];
}

export interface U3105PayrollYearOrganization {
  id: string;
  uuid?: string;
  organizationName?: string;
}

export interface U3105PayrollYearWithOrg extends U3105PayrollYearGet {
  organization?: U3105PayrollYearOrganization;
}

export interface U3105PayrollPeriodFormRow {
  id?: string;
  periodNumber: number;
  startAt: string;
  endAt: string;
  version?: number;
}

export type U3105PayrollYearFormRow = {
  id: string;
  yearCode: string;
  version: number;
  payrollPeriods: U3105PayrollPeriodFormRow[];
};

function utcMidnight(y: number, month0: number, day: number): Date {
  return new Date(Date.UTC(y, month0, day, 0, 0, 0, 0));
}

function utcEndOfDayCalendar(y: number, month0: number, day: number): Date {
  return new Date(Date.UTC(y, month0, day, 23, 59, 59, 0));
}

/** Valeur affichée dans les calendriers : jour civil UTC (comme U3104). */
function formPeriodDateUtcYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addUtcCalendarDaysFromDate(d: Date, deltaDays: number): Date {
  const y = d.getUTCFullYear();
  const m0 = d.getUTCMonth();
  const day = d.getUTCDate();
  return new Date(Date.UTC(y, m0, day + deltaDays, 0, 0, 0, 0));
}

/** Année civile servant de borne : `1er janvier (année+1)` exclus ; si le code est exactement `AAAA`, on utilise `AAAA`, sinon l’année UTC du premier lundi. */
export function u3105PayrollSequenceAnchorYear(yearCode: string, firstMondayUtcCalendarYear: number): number {
  const m = String(yearCode ?? "").trim().match(/^(\d{4})$/);
  if (m) return Number(m[1]);
  return firstMondayUtcCalendarYear;
}

function u3105ResolvedAnchorYearAndLimitMs(startMondayMidnightUtc: Date, yearCode: string): { anchorYear: number; limitMs: number } {
  let anchorYear = u3105PayrollSequenceAnchorYear(yearCode, startMondayMidnightUtc.getUTCFullYear());
  let limitMs = Date.UTC(anchorYear + 1, 0, 1, 0, 0, 0, 0);
  if (startMondayMidnightUtc.getTime() >= limitMs) {
    anchorYear = startMondayMidnightUtc.getUTCFullYear();
    limitMs = Date.UTC(anchorYear + 1, 0, 1, 0, 0, 0, 0);
  }
  return { anchorYear, limitMs };
}

/**
 * Nombre de périodes bihebdomadaires (lundi → +13 jours) dont le début est strictement avant le 1er janvier UTC
 * de l’année suivant l’ancre (code `AAAA` ou année du lundi). Utile pour valider l’assistant (26 ou 27 « naturel » ; la génération ne garde que 26).
 */
export function countU3105PayrollBiweeklyPeriodsForWizard(firstMondayYmd: string, yearCode: string): number {
  const head = String(firstMondayYmd ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return 0;
  const [ys, ms, ds] = head.split("-");
  const y = Number(ys);
  const mo = Number(ms) - 1;
  const dayNum = Number(ds);
  const start = utcMidnight(y, mo, dayNum);
  if (start.getUTCFullYear() !== y || start.getUTCMonth() !== mo || start.getUTCDate() !== dayNum) return 0;
  if (start.getUTCDay() !== 1) return 0;

  const { limitMs } = u3105ResolvedAnchorYearAndLimitMs(start, yearCode);
  let n = 0;
  let cur = start;
  while (cur.getTime() < limitMs) {
    n += 1;
    cur = addUtcCalendarDaysFromDate(cur, 14);
  }
  return n;
}

/**
 * Indique si la date (partie `yyyy-MM-dd` ou début de chaîne ISO) est un lundi civil UTC valide.
 */
export function isUtcMondayFirstPayrollDay(input: string): boolean {
  const head = String(input ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return false;
  const [ys, ms, ds] = head.split("-");
  const y = Number(ys);
  const mo = Number(ms) - 1;
  const dayNum = Number(ds);
  if (!Number.isInteger(y) || !Number.isInteger(mo + 1) || !Number.isInteger(dayNum)) return false;
  const dt = utcMidnight(y, mo, dayNum);
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo || dt.getUTCDate() !== dayNum) return false;
  return dt.getUTCDay() === 1;
}

/**
 * Périodes de paie **bihebdomadaires** : chaque période commence un lundi 00:00 UTC et se termine le dimanche
 * 23:59:59 UTC **deux semaines plus tard** (14 jours calendaires).
 * Borne : débuts **strictement avant** le 1er janvier UTC de l’année `(ancre + 1)`, avec `ancre = code année AAAA` si le code est
 * exactement quatre chiffres, sinon l’année civile UTC du lundi. Si le lundi est **on ou after** cette borne, on repousse l’ancre
 * sur l’année du lundi (évite 0 période). Le calendrier peut compter **26 ou 27** débuts avant la borne ; on **ne génère jamais la 27e** : au plus **26** périodes, numérotées 1…26.
 */
export function buildPayrollPeriodsFromFirstMonday(firstMondayYmd: string, yearCode: string): U3105PayrollPeriodFormRow[] {
  const head = String(firstMondayYmd ?? "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return [];
  const [ys, ms, ds] = head.split("-");
  const y = Number(ys);
  const mo = Number(ms) - 1;
  const dayNum = Number(ds);
  let start = utcMidnight(y, mo, dayNum);
  if (start.getUTCFullYear() !== y || start.getUTCMonth() !== mo || start.getUTCDate() !== dayNum) return [];
  if (start.getUTCDay() !== 1) return [];

  const { limitMs } = u3105ResolvedAnchorYearAndLimitMs(start, yearCode);
  const periods: U3105PayrollPeriodFormRow[] = [];
  let periodNumber = 1;

  while (start.getTime() < limitMs) {
    const endDay = addUtcCalendarDaysFromDate(start, 13);
    const end = utcEndOfDayCalendar(endDay.getUTCFullYear(), endDay.getUTCMonth(), endDay.getUTCDate());
    periods.push({
      periodNumber,
      startAt: formPeriodDateUtcYmd(start),
      endAt: formPeriodDateUtcYmd(end),
    });
    periodNumber += 1;
    start = addUtcCalendarDaysFromDate(start, 14);
  }

  if (periods.length === 27) {
    periods.splice(26, 1);
  }

  if (periods.length !== 26) {
    return [];
  }

  return periods.map((p, i) => ({ ...p, periodNumber: i + 1 }));
}

/** Deux périodes type exemple API (squelette pour création sans assistant). */
export function buildDefaultPayrollPeriodsForYear(year: number): U3105PayrollPeriodFormRow[] {
  return [
    {
      periodNumber: 1,
      startAt: new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0)).toISOString(),
      endAt: new Date(Date.UTC(year, 0, 14, 23, 59, 59, 0)).toISOString(),
    },
    {
      periodNumber: 2,
      startAt: new Date(Date.UTC(year, 0, 15, 0, 0, 0, 0)).toISOString(),
      endAt: new Date(Date.UTC(year, 0, 28, 23, 59, 59, 0)).toISOString(),
    },
  ];
}

export function sortPeriodsByNumber(periods: U3105PayrollPeriodApi[]): U3105PayrollPeriodApi[] {
  return [...periods].sort((a, b) => (a.periodNumber ?? 0) - (b.periodNumber ?? 0));
}

export function normalizeU3105FromApi(row: U3105PayrollYearGet | U3105PayrollYearWithOrg): U3105PayrollYearFormRow {
  const sorted = sortPeriodsByNumber(row.payrollPeriods ?? []);
  return {
    id: row.id,
    yearCode: row.yearCode ?? "",
    version: row.version ?? 0,
    payrollPeriods: sorted.map((p) => ({
      id: p.id,
      periodNumber: p.periodNumber,
      startAt: p.startAt ?? "",
      endAt: p.endAt ?? "",
      version: p.version,
    })),
  };
}

export function formToSearchRow(f: U3105PayrollYearFormRow): U3105PayrollYearSearchRow {
  return {
    id: f.id,
    yearCode: f.yearCode,
    version: f.version,
  };
}

export function createEmptyU3105FormRow(): U3105PayrollYearFormRow {
  const y = new Date().getUTCFullYear();
  return {
    id: "",
    yearCode: "",
    version: 0,
    payrollPeriods: buildDefaultPayrollPeriodsForYear(y),
  };
}

export interface U3105PayrollPeriodCreateBody {
  periodNumber: number;
  startAt: string;
  endAt: string;
}

export interface U3105PayrollPeriodUpdateBody extends U3105PayrollPeriodCreateBody {
  id?: string;
  version?: number;
}

export interface U3105PayrollYearCreateBody {
  yearCode: string;
  payrollPeriods: U3105PayrollPeriodCreateBody[];
}

export interface U3105PayrollYearUpdateBody {
  id: string;
  yearCode: string;
  version: number;
  payrollPeriods: U3105PayrollPeriodUpdateBody[];
}

export interface U3105PayrollYearDeleteBody {
  id: string;
  version: number;
}

function toIsoBoundary(value: string): string {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.includes("T")) return new Date(s).toISOString();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return new Date(`${ymd}T00:00:00.000Z`).toISOString();
  }
  return new Date(s).toISOString();
}

function toIsoEndOfDayFromYmd(value: string): string {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.includes("T")) return new Date(s).toISOString();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return new Date(`${ymd}T23:59:59.000Z`).toISOString();
  }
  return new Date(s).toISOString();
}

export function buildCreateBody(data: U3105PayrollYearFormRow): U3105PayrollYearCreateBody {
  return {
    yearCode: data.yearCode.trim(),
    payrollPeriods: data.payrollPeriods.map((p) => ({
      periodNumber: p.periodNumber,
      startAt: toIsoBoundary(p.startAt),
      endAt: toIsoEndOfDayFromYmd(p.endAt),
    })),
  };
}

export function buildUpdateBody(data: U3105PayrollYearFormRow): U3105PayrollYearUpdateBody {
  return {
    id: data.id,
    yearCode: data.yearCode.trim(),
    version: data.version,
    payrollPeriods: data.payrollPeriods.map((p) => {
      const base: U3105PayrollPeriodUpdateBody = {
        periodNumber: p.periodNumber,
        startAt: toIsoBoundary(p.startAt),
        endAt: toIsoEndOfDayFromYmd(p.endAt),
      };
      if (p.id) {
        base.id = p.id;
        if (p.version !== undefined) {
          base.version = p.version;
        }
      }
      return base;
    }),
  };
}

export function buildDeleteBody(data: U3105PayrollYearFormRow): U3105PayrollYearDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const U3105_ERROR_YEAR_CODE_DUPLICATE = "BU310501";
export const U3105_ERROR_PERIOD_NOT_FOUND = "BU310502";
export const U3105_ERROR_ENTITY_ID_MISMATCH = "B00008";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3105PayrollYearCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3105PayrollYearUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3105PayrollYearDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3105_ERROR_YEAR_CODE_DUPLICATE:
      errorResult.fieldErrors["yearCode"] = t("errYearCodeDuplicate", { ns: nameSpace });
      break;
    case U3105_ERROR_PERIOD_NOT_FOUND:
      errorResult.fieldErrors["payrollPeriods"] = t("errPeriodNotFound", { ns: nameSpace });
      break;
    case U3105_ERROR_ENTITY_ID_MISMATCH:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errEntityIdMismatch", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
